import type { CourseSetting, SpaceType } from "@circle-react/types/Space";
import { useReducer } from "react";

import { DEFAULT_COURSE_TYPE } from "@/react/components/Spaces/CourseSpace/CourseTypePicker/constants";

import { STEP } from "../constants";

export type Step =
  | { id: typeof STEP.type }
  | { id: typeof STEP.course }
  | {
      id: typeof STEP.form;
      postType: SpaceType;
      courseSetting: CourseSetting;
    };

type Action =
  | { type: "selectType"; postType: SpaceType }
  | { type: "confirmCourse"; courseSetting: CourseSetting }
  | { type: "back" }
  | { type: "reset"; initialPostType?: SpaceType };

const defaultCourseSetting: CourseSetting = {
  course_type: DEFAULT_COURSE_TYPE,
};

const buildInitialStep = (initialPostType?: SpaceType): Step => {
  if (!initialPostType) return { id: STEP.type };
  if (initialPostType === "course") return { id: STEP.course };
  return {
    id: STEP.form,
    postType: initialPostType,
    courseSetting: defaultCourseSetting,
  };
};

const reducer = (step: Step, action: Action): Step => {
  switch (action.type) {
    case "selectType":
      if (action.postType === "course") return { id: STEP.course };
      return {
        id: STEP.form,
        postType: action.postType,
        courseSetting: defaultCourseSetting,
      };
    case "confirmCourse":
      return {
        id: STEP.form,
        postType: "course",
        courseSetting: action.courseSetting,
      };
    case "back":
      if (step.id === STEP.form && step.postType === "course") {
        return { id: STEP.course };
      }
      return { id: STEP.type };
    case "reset":
      return buildInitialStep(action.initialPostType);
  }
};

export const useCreateSpaceState = (initialPostType?: SpaceType) => {
  const [step, dispatch] = useReducer(
    reducer,
    initialPostType,
    buildInitialStep,
  );

  return {
    step,
    selectType: (postType: SpaceType) =>
      dispatch({ type: "selectType", postType }),
    confirmCourse: (courseSetting: CourseSetting) =>
      dispatch({ type: "confirmCourse", courseSetting }),
    back: () => dispatch({ type: "back" }),
    reset: () => dispatch({ type: "reset", initialPostType }),
  };
};
